import { ActionsHub } from "VersionControl/Scripts/Actions/PullRequestReview/ActionsHub";
import { SourcesHub } from "VersionControl/Scripts/Sources/SourcesHub";
import { StoresHub } from "VersionControl/Scripts/Stores/PullRequestReview/StoresHub";

import { createCommitMessage, parseCommitMessage } from "VersionControl/Scenarios/PullRequestDetail/Stores/PullRequestCompleteMergeStore";

import * as CustomerIntelligenceConstants from "VersionControl/Scripts/CustomerIntelligenceConstants";
import { publishEvent, TelemetryEventData } from "VSS/Telemetry/Services";

import { IMergePolicySetting } from "VersionControl/Scenarios/PullRequestDetail/Stores/ClientPolicyEvaluationStore";
import { RepositoryContext } from "VersionControl/Scripts/RepositoryContext";

import {
    MergeOptionsDeleteSourceCheckboxMode,
    MergeOptionsSquashMergeCheckboxMode,
    MergeOptionsTransitionWorkItemsCheckboxMode,
} from "VersionControl/Scripts/Generated/TFS.VersionControl.WebAccess.Contracts";

export class PullRequestCompleteMergeActionCreator {
    private _actionsHub: ActionsHub;
    private _storesHub: StoresHub;
    private _sourcesHub: SourcesHub;
    private _repositoryContext: RepositoryContext;

    public constructor(repositoryContext: RepositoryContext, actionsHub: ActionsHub, sourcesHub: SourcesHub, storesHub: StoresHub) {
        this._actionsHub = actionsHub;
        this._storesHub = storesHub;
        this._sourcesHub = sourcesHub;
        this._repositoryContext = repositoryContext;
    }

    public openCompleteMergeDialog(){
        const prefs = this._storesHub.userPreferencesStore.getPreferences();
        const pullRequestDetail = this._storesHub.pullRequestDetailStore.getPullRequestDetail();

        let shouldDeleteSourceBranch = prefs ? prefs.mergeOptionsDeleteSourceCheckboxMode === MergeOptionsDeleteSourceCheckboxMode.Checked : null;
        // over-write preferences if delete permissions would prevent deletion
        shouldDeleteSourceBranch = pullRequestDetail.canDeleteSourceBranch ? shouldDeleteSourceBranch : false;

        const shouldTransitionWorkItems = prefs ? prefs.mergeOptionsTransitionWorkItemsCheckboxMode === MergeOptionsTransitionWorkItemsCheckboxMode.Checked : null;
        const mergePolicySetting = this._getMergePolicySettings();
        const shouldSquashMerge = this._getShouldSquashMerge(mergePolicySetting);
        const userPrefMergeStrategy = prefs && prefs.mergeOptionsMergeStrategy;

        this._actionsHub.completionDialogOpened.invoke({
            associatedWorkItemIds: this._storesHub.relatedWorkItemsStore.getWorkItems(),
            shouldDeleteSourceBranch,
            shouldSquashMerge,
            userPrefMergeStrategy,
            shouldTransitionWorkItems,
            canTransitionWorkItems: true,
            mergePolicySetting,
            pullRequestDetail,
        });
    }

    public updateMergeTitle(mergeTitle: string): void {
        this._actionsHub.mergeTitleUpdated.invoke({mergeTitle});
    }

    public updateMergeDescription(mergeDescription: string): void {
        this._actionsHub.mergeDescriptionUpdated.invoke({mergeDescription});
    }

    public updateBypassReason(bypassReason: string): void {
        this._actionsHub.bypassReasonUpdated.invoke({bypassReason});
    }

    public updateBypassPolicy(shouldEnable: boolean): void {
        this._actionsHub.bypassUpdated.invoke({shouldEnable});
    }

    public publishTelemetry(): void {
        const store = this._storesHub.pullRequestCompletionOptionsStore;
        const completionOptions = store.getCompletionOptions();
        const commitMessage = store.getCommitMessage();

        const parsedCommitMessage = parseCommitMessage(commitMessage);
        const mergeDetails = store.getMergeDescription();

        const defaultTitle = store.getDefaultMergeTitle();
        const defaultDescription = store.getDefaultMergeDescription();
        const defaultWorkItems = store.getDefaultWorkItems();
        const defaultDetails = createCommitMessage({
            formattedDescription: defaultDescription, 
            formattedWorkItems: defaultWorkItems,
        });

        // there is an issue here because we lose the originalDetails if the user auto-completes and then cancels
        const completeMergeEvent = new TelemetryEventData(
            CustomerIntelligenceConstants.VERSION_CONTROL_AREA,
            CustomerIntelligenceConstants.PULL_REQUEST_COMPLETE_MERGE, {
                TitleWasChanged: parsedCommitMessage.formattedTitle !== defaultTitle,
                DetailsWasChanged: (mergeDetails !== defaultDetails),
                DetailsLength: mergeDetails.length,
                DescriptionWasChanged: parsedCommitMessage.formattedDescription !== defaultDescription,
                DescriptionLength: parsedCommitMessage.formattedDescription.length,
                WorkItemsWereChanged: parsedCommitMessage.formattedWorkItems !== defaultWorkItems,
                WorkItemsLength: parsedCommitMessage.formattedWorkItems.length,
                UncheckedDeleteSource: !completionOptions.deleteSourceBranch,
                TransitionWorkItemsSelected: completionOptions.transitionWorkItems,
                SquashMergeSelected: completionOptions.squashMerge,
                BypassPoliciesSelected: completionOptions.bypassPolicy,
            });
        publishEvent(completeMergeEvent);
    }

    private _getShouldSquashMerge(mergePolicySetting: IMergePolicySetting): boolean {
        const prefs = this._storesHub.userPreferencesStore.getPreferences();

        const shouldSquashMerge = prefs ? prefs.mergeOptionsSquashMergeCheckboxMode === MergeOptionsSquashMergeCheckboxMode.Checked : null;

        // over-write preferences if the merge policy requires a specific value and the policy isn't overridden
        return mergePolicySetting.useSquashMerge ? true : shouldSquashMerge;
    }

    private _getMergePolicySettings(): IMergePolicySetting {
        return this._storesHub.clientPolicyEvaluationStore.state.mergePolicySetting;
    }
}
